import { api } from '@/shared/api/client';
import type {
  Subscription, SubscriptionBalance, SubscriptionDetail,
  MemberOut, InviteOut, InviteLinkOut, InviteDetailOut, AcceptInviteOut,
  ExitRequestOut, ManualCorrectionResult,
} from './types';

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateSubscriptionPayload {
  name: string;
  icon: string;
  type: 'subscription' | 'collection';
  total_price: number;
  currency: string;
  period: { interval: number; unit: string };
  billing_date: string | null;
  deadline_shift: number;
  split_mode: string;
  privacy: string;
  show_history: boolean;
  draft_step?: string;
  member_slots: { name: string; amount: number }[];
}

export const subscriptionApi = {
  list: (role: 'holder' | 'participant') =>
    api.get<Page<Subscription>>(`/api/v1/subscriptions?role=${role}`),
  getDetail: (id: string) => api.get<SubscriptionDetail>(`/api/v1/subscriptions/${id}`),
  get: (id: string) => api.get<Subscription>(`/api/v1/subscriptions/${id}`),
  getBalances: (id: string) => api.get<SubscriptionBalance>(`/api/v1/subscriptions/${id}/balances`),
  getMembers: (id: string) => api.get<Page<MemberOut>>(`/api/v1/subscriptions/${id}/members`),
  getInvite: (id: string) => api.get<InviteOut | null>(`/api/v1/subscriptions/${id}/invite`),
  createInvite: (id: string) => api.post<InviteLinkOut>(`/api/v1/subscriptions/${id}/invites`, {}),
  create: (payload: CreateSubscriptionPayload) =>
    api.post<Subscription>('/api/v1/subscriptions', payload),
  patch: (id: string, payload: Partial<CreateSubscriptionPayload>) =>
    api.patch<Subscription>(`/api/v1/subscriptions/${id}`, payload),
  activate: (id: string) =>
    api.post<Subscription>(`/api/v1/subscriptions/${id}/activate`, {}),
  deleteDraft: (id: string) =>
    api.delete<void>(`/api/v1/subscriptions/${id}`),
  changePrice: (id: string, payload: { new_price: number; scenario: string; effective_date: string }) =>
    api.post<void>(`/api/v1/subscriptions/${id}/price`, payload),
  close: (id: string) =>
    api.post<void>(`/api/v1/subscriptions/${id}/close`, {}),
  removeMember: (membershipId: string) =>
    api.delete<void>(`/api/v1/memberships/${membershipId}`),
  getInviteByCode: (code: string) =>
    api.get<InviteDetailOut>(`/api/v1/invites/${code}`),
  acceptInvite: (code: string) =>
    api.post<AcceptInviteOut>(`/api/v1/invites/${code}/accept`, {}),
  declineInvite: (code: string) =>
    api.post<void>(`/api/v1/invites/${code}/decline`, {}),
  requestExit: (id: string) =>
    api.post<void>(`/api/v1/subscriptions/${id}/exit-requests`, {}),
  manualCorrection: (
    id: string,
    payload: { participant_id: string; type: 'correction' | 'writeoff'; amount: number; increase?: boolean; comment?: string },
  ) => api.post<ManualCorrectionResult>(`/api/v1/subscriptions/${id}/manual-corrections`, payload),
  listExitRequests: (id: string) =>
    api.get<ExitRequestOut[]>(`/api/v1/subscriptions/${id}/exit-requests`),
  remind: (id: string) => api.post<{ notified_count: number }>(`/api/v1/subscriptions/${id}/remind`, {}),
  approveExit: (exitRequestId: string, payload: { exit_date: string; scenario: 'prorate' | 'settle' }) =>
    api.post<void>(`/api/v1/exit-requests/${exitRequestId}/approve`, payload),
  declineExit: (exitRequestId: string) =>
    api.post<void>(`/api/v1/exit-requests/${exitRequestId}/decline`, {}),
  updateSettings: (id: string, payload: { payment_confirmation_required?: boolean; reminder_offsets?: number[] }) =>
    api.patch<void>(`/api/v1/subscriptions/${id}/settings`, payload),
};
